import { FC, useEffect, useMemo, useState } from "react";
import useIsComponentMounted from "../../../common/hooks/use-is-component-mounted";
import { useAppContext } from "../../../context/app.context";
import appTranslationsHelper from "../../../languages/app-translations.helper";
import useDocumentTitle from "../../../common/hooks/use-document-title";
import orderBreadcrumbsHelper from "../common/breadcrumbs/order-breadcrumbs.helper";
import useOrderCargoOrderFinishedList from "../../../common/services/order/cargo-order/finished/list/use-order-cargo-order-finished-list";
import useAbort from "../../../common/hooks/use-abort";
import OrderCargoOrderFinishedListParams from "../../../common/services/order/cargo-order/finished/list/order-cargo-order-finished-list-params";
import orderFinishedListingLoadParamsFactory from "./common/order-finished-listing-load-params.factory";
import usePagination from "../../../common/hooks/use-pagination";
import useOrderFinishedListingFiltersDao from "./filter/use-order-finished-listing-filters.dao";
import OrderFinishedListingFilter, {
  OrderFinishedListingOrderStartDateFilter,
} from "./common/types/order-finished-listing-filter";
import OrderFinishedListingSortKey from "./common/types/order-finished-listing-sort-key";
import orderTranslationsHelper from "../../../languages/order-translations.helper";
import OrderFinishedListingItem from "./common/types/order-finished-listing-item";
import HeadingComponent from "../../../common/components/heading/heading.component";
import OrderFinishedListingFilterComponent from "./filter/order-finished-listing-filters.component";
import CardComponent from "../../../common/components/card/card.component";
import OrderFinishedListingTableComponent from "./table/order-finished-listing-table.component";
import ListingPaginationComponent from "../../../common/components/listing/pagination/listing-pagination.component";
import orderFinishedListingItemFactory from "./common/order-finished-listing-item.factory";
import ButtonComponent from "../../../common/components/button/button.component";
import OrderCargoOrderFinishedDownload from "../../../common/services/order/cargo-order/finished/download/order-cargo-order-finished-download";
import OrderCargoOrderFinishedDownloadParams from "../../../common/services/order/cargo-order/finished/download/order-cargo-order-finished-download-params";
import fileDownloadService from "../../../common/utils/file-download/file-download.service";
import notificationService from "../../../common/utils/notification/notification.service";
import OrderCargoOrderFinishedDownloadError from "../../../common/services/order/cargo-order/finished/download/order-cargo-order-finished-download-error";
import orderFinishedListingReportLoadParamsFactory from "./common/order-finished-listing-report-load-params.factory";
import orderFinishedListingHelper from "./common/order-finished-listing.helper";
import StatusLegendComponent from "../../../common/components/status-legend/status-legend.component";
import OrderFinishedListingFilterType from "./common/types/order-finished-listing-filter-type";
import useOrderFinishedCargoOrdersCount from "../../../common/services/order/finished-cargo-orders/count/use-order-finished-cargo-orders-count";
import orderService from "../../../common/services/order/order.service";

type OrderFinishedListingProps = {};

const OrderFinishedListingComponent: FC<OrderFinishedListingProps> = () => {
  const isComponentMounted = useIsComponentMounted();
  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();
  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.orderFinishedListing);

  useEffect(() => {
    const breadcrumbs = orderBreadcrumbsHelper.getFinishedListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const orderFinishedCargoOrdersCount = useOrderFinishedCargoOrdersCount();
  const orderFinishedCargoOrdersCountAbort = useAbort();

  const finishedOrderList = useOrderCargoOrderFinishedList();
  const listingItemsAbort = useAbort();
  const listingReportAbort = useAbort();

  const filtersDao = useOrderFinishedListingFiltersDao();

  const { page, pageSize, setPage, setPageSize } = usePagination({
    defaultPageSize: filtersDao.getPageSize(),
    defaultPage: filtersDao.getPage(),
  });

  const [expandedRowsOrderUuids, setExpandedRowsOrderUuids] = useState<
    OrderFinishedListingItem["uuid"][]
  >([]);

  const [sortKey, setSortKey] = useState<OrderFinishedListingSortKey>(
    () =>
      filtersDao.getSortKey() ??
      OrderFinishedListingSortKey.CARGO_COMPANY_INTERNAL_ID_DESC,
  );

  const [filters, setFilters] = useState<OrderFinishedListingFilter[]>(() => {
    const existingFilters = filtersDao.getFilters();

    if (existingFilters.length === 0) {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const orderStartDateFilter: OrderFinishedListingOrderStartDateFilter = {
        type: OrderFinishedListingFilterType.ORDER_START_DATE,
        value: {
          from: startOfMonth,
          to: endOfMonth,
        },
      };

      filtersDao.saveFilters([orderStartDateFilter], sortKey, page, pageSize);
      return [orderStartDateFilter];
    }

    return existingFilters;
  });

  const filtersKey = JSON.stringify(filters);

  const [isReportFetching, setIsReportFetching] = useState(false);

  const loadListingItems = async () => {
    const params: OrderCargoOrderFinishedListParams =
      orderFinishedListingLoadParamsFactory.create(
        page,
        pageSize,
        filters,
        sortKey,
      );

    const countParams =
      orderFinishedListingLoadParamsFactory.createCountParams(params);

    finishedOrderList.load(params, listingItemsAbort.signal);

    orderFinishedCargoOrdersCount.load(
      countParams,
      orderFinishedCargoOrdersCountAbort.signal,
    );
  };

  const saveFilters = () => {
    filtersDao.saveFilters(filters, sortKey, page, pageSize);
  };
  const listingItems: OrderFinishedListingItem[] = useMemo(() => {
    return orderFinishedListingItemFactory.createListingItems(
      finishedOrderList.data.data,
    );
  }, [finishedOrderList.data.data]);

  const translations = orderTranslationsHelper.getFinishedListingTranslations();

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    saveFilters();
  }, [filters, sortKey, page, pageSize]);

  useEffect(() => {
    window.scroll({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    setExpandedRowsOrderUuids([]);
    loadListingItems();

    return () => listingItemsAbort.revoke();
  }, [page, pageSize, filters, sortKey]);

  const onTableRowClick = (
    listingItemUuid: OrderFinishedListingItem["uuid"],
  ) => {
    const expandRow = () => {
      setExpandedRowsOrderUuids((curr) => [...curr, listingItemUuid]);
    };

    const collapseRow = () => {
      const filteredExpandedRows = expandedRowsOrderUuids.filter(
        (expandedOrderUuid) => expandedOrderUuid !== listingItemUuid,
      );

      setExpandedRowsOrderUuids(filteredExpandedRows);
    };

    const isRowExpanded = expandedRowsOrderUuids.includes(listingItemUuid);

    if (isRowExpanded) {
      collapseRow();
      return;
    }

    expandRow();
  };

  const downloadReportFile = (report: OrderCargoOrderFinishedDownload) => {
    fileDownloadService.downloadFromBlob(report.data, report.filename);
  };

  const onReportFetchSuccess = async (
    report: OrderCargoOrderFinishedDownload,
  ) => {
    downloadReportFile(report);
  };

  const onReportFetchFailure = (
    error: OrderCargoOrderFinishedDownloadError,
  ) => {
    notificationService.error(error.message);
  };

  const fetchReport = async () => {
    setIsReportFetching(true);

    const dateFilter = filters.find(
      (filter) =>
        filter.type === OrderFinishedListingFilterType.ORDER_START_DATE,
    );

    if (dateFilter) {
      const fromDate = new Date(dateFilter.value.from);
      const toDate = new Date(dateFilter.value.to);

      const sixMonthsLater = new Date(fromDate);
      sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);

      if (toDate > sixMonthsLater) {
        notificationService.error(
          translations.report.dateRangeLimitErrorMessage,
        );
        setIsReportFetching(false);
        return;
      }
    }

    const params: OrderCargoOrderFinishedDownloadParams =
      orderFinishedListingReportLoadParamsFactory.create(filters, sortKey);

    try {
      const cargoOrderFinishedListReport: OrderCargoOrderFinishedDownload =
        await orderService.getFinishedDownload(
          params,
          listingReportAbort.signal,
        );

      onReportFetchSuccess(cargoOrderFinishedListReport);
    } catch (error) {
      onReportFetchFailure(error as OrderCargoOrderFinishedDownloadError);
    } finally {
      setIsReportFetching(false);
    }
  };

  const ReportDownloadButton = (
    <ButtonComponent
      onClick={fetchReport}
      type="primary"
      isLoading={isReportFetching}
      title={translations.report.downloadButtonTitle}
      idForTesting="order-finished-listing-download-report-button"
    >
      {translations.report.downloadButtonLabel}
    </ButtonComponent>
  );

  const statusOptions = orderFinishedListingHelper.getStatusOptions();

  return (
    <>
      <HeadingComponent
        actions={[ReportDownloadButton]}
        title={translations.header.headingLabel}
      />
      <StatusLegendComponent statusData={statusOptions} />
      <OrderFinishedListingFilterComponent
        filters={filters}
        sortKey={sortKey}
        onFiltersChange={(filters) => {
          setFilters(filters);
          setPage(1);
        }}
        onSortKeyChange={(sortKey) => {
          setSortKey(sortKey);
          setPage(1);
        }}
      />
      <CardComponent classNames={{ root: "mt-4" }}>
        <OrderFinishedListingTableComponent
          listingItems={listingItems}
          isError={finishedOrderList.isError}
          isLoading={finishedOrderList.isLoading}
          expandedRowsOrderUuids={expandedRowsOrderUuids}
          onRowClick={onTableRowClick}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={orderFinishedCargoOrdersCount.data}
          resetTotalResultsKey={filtersKey}
          paginationCountHide
        />
      </CardComponent>
    </>
  );
};

export default OrderFinishedListingComponent;
